import { useMemo } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { summarizeSession } from '@movmania/core'
import { questLabel } from '../components/QuestList.js'
import { Button, Card, Figure, Screen, ScreenBody, ScreenFooter } from '../components/Ui.js'
import { photo } from '../lib/photos.js'
import { useGame } from '../lib/useGame.js'
import { useT } from '../lib/useT.js'
import { useStore } from '../store/useStore.js'

/**
 * La "fin" de la règle pic-fin : l'app ne se termine jamais sur rien.
 * On referme toujours sur ce qui vient d'être accompli, puis on renvoie chez soi.
 */
export function SessionDone() {
  const t = useT()
  const navigate = useNavigate()
  const game = useGame()
  const activities = useStore((s) => s.activities)

  const summary = useMemo(
    () => summarizeSession(activities, game.weeklyGoalMin),
    [activities, game.weeklyGoalMin],
  )
  const completed = game.quests.filter((quest) => quest.done)

  // Arriver ici sans aucune séance (rechargement, lien direct) : on renvoie
  // à l'accueil par le routeur, jamais par un effet de bord dans le rendu.
  if (!summary) return <Navigate to="/" replace />

  return (
    <Screen>
      <ScreenBody className="pb-6">
        <div className="relative -mx-5 h-80">
          <img
            src={photo('done')}
            alt=""
            className="absolute inset-0 size-full object-cover object-[50%_25%]"
          />
          <div className="from-canvas absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t to-transparent" />
        </div>

        <div className="relative -mt-14 text-center">
          {/* Halo vert derrière la coche : le seul effet de lueur de l'app,
              réservé à ce moment. */}
          <div className="animate-pop bg-green-soft mx-auto mb-6 grid size-28 place-items-center rounded-full">
            <div className="bg-green text-surface grid size-18 place-items-center rounded-full shadow-[0_12px_28px_-8px_rgb(47_93_70/0.6)]">
              <Check size={34} strokeWidth={3} aria-hidden="true" />
            </div>
          </div>

          <div className="animate-rise">
            <Figure value={summary.minutes} size="display" />
            <p className="text-caption text-ink-3 mt-1">{t('done.minutesLogged')}</p>

            <h1 className="text-title mt-8 font-bold">
              {summary.goalReached ? t('done.goalTitle') : t('done.title')}
            </h1>
            <p className="text-body text-ink-2 mx-auto mt-2 max-w-xs">
              {summary.goalReached
                ? t('done.goalBody')
                : t('done.body', { count: summary.remainingToGoal })}
            </p>
          </div>
        </div>

        {completed.length > 0 && (
          <Card tone="green" className="animate-rise mt-8">
            <p className="text-caption text-ink-2 mb-3 font-bold">{t('done.questsDone')}</p>
            <ul className="space-y-2">
              {completed.map((quest) => (
                <li key={quest.id} className="text-body flex items-center gap-3">
                  <span className="bg-green text-surface grid size-6 shrink-0 place-items-center rounded-full">
                    <Check size={13} strokeWidth={3} aria-hidden="true" />
                  </span>
                  {questLabel(t, quest)}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </ScreenBody>

      <ScreenFooter safe>
        <Button full onClick={() => navigate('/', { replace: true })}>
          {t('done.back')}
        </Button>
      </ScreenFooter>
    </Screen>
  )
}
